import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckCircleIcon,
  XCircleIcon,
  Stars01Icon,
  MinusCircleIcon,
  AlertCircleIcon,
} from '@untitledui-icons/react/line'
import { Tooltip, TooltipTrigger } from '@/components/ui/Tooltip'
import { cn } from '@/lib/utils'
import type { QALabel, BusinessOutcome } from '@/types/conversation'

interface QALabelBadgeProps {
  meta: string | undefined
  className?: string
  showTooltip?: boolean
}

const getStyleForOutcome = (outcome: BusinessOutcome, isLLM: boolean) => {
  // Define base styles for each outcome
  const baseStyles = {
    success: {
      icon: CheckCircleIcon,
      bgColor: 'bg-green-100 dark:bg-green-900',
      textColor: 'text-green-700 dark:text-green-300',
      borderColor: 'border-green-300 dark:border-green-700',
    },
    partial: {
      icon: MinusCircleIcon,
      bgColor: 'bg-yellow-100 dark:bg-yellow-900',
      textColor: 'text-yellow-700 dark:text-yellow-300',
      borderColor: 'border-yellow-300 dark:border-yellow-700',
    },
    failure: {
      icon: XCircleIcon,
      bgColor: 'bg-red-100 dark:bg-red-900',
      textColor: 'text-red-700 dark:text-red-300',
      borderColor: 'border-red-300 dark:border-red-700',
    },
    neutral: {
      icon: AlertCircleIcon,
      bgColor: 'bg-gray-100 dark:bg-gray-800',
      textColor: 'text-gray-600 dark:text-gray-300',
      borderColor: 'border-gray-300 dark:border-gray-600',
    },
  }

  const style = baseStyles[outcome] || baseStyles.neutral

  // LLM labels get a purple tint overlay
  if (isLLM) {
    return {
      ...style,
      bgColor: `bg-gradient-to-r ${style.bgColor.replace('bg-', 'from-')} to-purple-100 dark:to-purple-900`,
      borderColor: 'border-purple-300 dark:border-purple-700',
    }
  }

  return style
}

export function QALabelBadge({ meta, className, showTooltip = true }: QALabelBadgeProps) {
  const { t } = useTranslation()

  // Parse QA label from meta
  const qaLabel = useMemo((): QALabel | null => {
    if (!meta) return null
    try {
      const parsed = JSON.parse(meta)
      return parsed.qa_label || null
    } catch {
      return null
    }
  }, [meta])

  if (!qaLabel) return null

  const isLLMLabel = qaLabel.source === 'llm'
  const style = getStyleForOutcome(qaLabel.business_outcome, isLLMLabel)
  const Icon = style.icon

  const badge = (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        style.bgColor,
        style.textColor,
        style.borderColor,
        className
      )}
    >
      {isLLMLabel && <Stars01Icon className="h-3 w-3 text-purple-500 dark:text-purple-400" />}
      <Icon className="h-3.5 w-3.5" />
      <span className="sr-only md:not-sr-only">
        {t(`resources.conversations.qa_label.business_outcome.${qaLabel.business_outcome}`)}
      </span>
    </span>
  )

  if (!showTooltip) {
    return badge
  }

  // Build tooltip content
  const tooltipParts: string[] = []
  if (isLLMLabel) {
    tooltipParts.push(t('resources.conversations.qa_label.llm_generated'))
    if (qaLabel.model) {
      tooltipParts[0] += ` (${qaLabel.model})`
    }
  }

  // Show training suitability
  tooltipParts.push(
    `${t('resources.conversations.qa_label.training_suitability.label')}: ${t(
      `resources.conversations.qa_label.training_suitability.${qaLabel.training_suitability}`
    )}`
  )

  // Show adherence score
  tooltipParts.push(`${t('resources.conversations.qa_label.adherence_score.label')}: ${qaLabel.adherence_score}/10`)

  // Show tooltip
  return (
    <TooltipTrigger delay={300}>
      {badge}
      <Tooltip>{tooltipParts.join(' · ')}</Tooltip>
    </TooltipTrigger>
  )
}

export default QALabelBadge
